/**
 * Shared label / colour helpers for the CallScribe call-log columns and the
 * call-detail drawer. Extracted so the row and the drawer stay visually in sync.
 */
import {
  CallScribeCallStatus,
  CallScribeSentiment,
  CallScribeTimeEntryStatus,
} from 'services/callScribe/types';

import { Theme } from '@mui/material';

import { csTokens } from 'components/CallScribe/callScribeTheme';
import { parseUtcInstant } from 'components/CallScribe/Recents/recentsHelpers';

/**
 * Status and sentiment tones resolve through the CallScribe palette group so a
 * white-label palette swap recolours every chip. Colour is never the only
 * signal — always pair these with an icon and a text label.
 */
export const statusTone = (theme: Theme, status: CallScribeCallStatus): string => {
  const t = csTokens(theme);
  switch (status) {
    case CallScribeCallStatus.Completed:
      return t.live;
    case CallScribeCallStatus.Answered:
      return t.info;
    case CallScribeCallStatus.Failed:
      return t.end;
    case CallScribeCallStatus.NoAnswer:
    case CallScribeCallStatus.Busy:
      return t.warn;
    default:
      return t.neutral;
  }
};

export const sentimentTone = (theme: Theme, sentiment: CallScribeSentiment): string => {
  const t = csTokens(theme);
  switch (sentiment) {
    case CallScribeSentiment.Positive:
      return t.live;
    case CallScribeSentiment.Negative:
      return t.end;
    case CallScribeSentiment.Mixed:
      return t.warn;
    case CallScribeSentiment.Neutral:
      return t.info;
    default:
      return t.neutral;
  }
};

export const timeEntryTone = (theme: Theme, status: CallScribeTimeEntryStatus): string => {
  const t = csTokens(theme);
  switch (status) {
    case CallScribeTimeEntryStatus.Posted:
      return t.live;
    case CallScribeTimeEntryStatus.Failed:
      return t.end;
    case CallScribeTimeEntryStatus.Rejected:
      return t.neutral;
    default:
      return t.warn;
  }
};

/** Human-readable label per `CallScribeCallStatus`. */
export const STATUS_LABEL: Record<CallScribeCallStatus, string> = {
  [CallScribeCallStatus.Initiated]: 'Initiated',
  [CallScribeCallStatus.Ringing]: 'Ringing',
  [CallScribeCallStatus.Answered]: 'Answered',
  [CallScribeCallStatus.Completed]: 'Completed',
  [CallScribeCallStatus.Failed]: 'Failed',
  [CallScribeCallStatus.NoAnswer]: 'No answer',
  [CallScribeCallStatus.Busy]: 'Busy',
  [CallScribeCallStatus.Cancelled]: 'Cancelled',
  [CallScribeCallStatus.Unknown]: 'Unknown',
};

export const statusLabel = (status: CallScribeCallStatus): string =>
  STATUS_LABEL[status] ?? 'Unknown';


export const SENTIMENT_LABEL: Record<CallScribeSentiment, string> = {
  [CallScribeSentiment.Unknown]: 'Unknown',
  [CallScribeSentiment.Positive]: 'Positive',
  [CallScribeSentiment.Neutral]: 'Neutral',
  [CallScribeSentiment.Negative]: 'Negative',
  [CallScribeSentiment.Mixed]: 'Mixed',
};

export const ESCALATION_LABEL: Record<number, string> = {
  0: 'None',
  1: 'Low',
  2: 'Medium',
  3: 'High',
};


export const LINK_LABEL: Record<CallScribeTimeEntryStatus, string> = {
  [CallScribeTimeEntryStatus.Suggested]: 'Suggested',
  [CallScribeTimeEntryStatus.Posted]: 'Posted',
  [CallScribeTimeEntryStatus.Rejected]: 'Rejected',
  [CallScribeTimeEntryStatus.Failed]: 'Failed',
  [CallScribeTimeEntryStatus.Claimed]: 'Posting',
};

/** Formats an integer number of seconds as `m:ss` (or `h:mm:ss` past an hour). */
export const formatDurationLabel = (totalSeconds: number | null | undefined): string => {
  if (typeof totalSeconds !== 'number' || totalSeconds < 0) {
    return '—';
  }
  const seconds = Math.floor(totalSeconds % 60);
  const minutes = Math.floor((totalSeconds / 60) % 60);
  const hours = Math.floor(totalSeconds / 3600);
  const pad = (n: number): string => n.toString().padStart(2, '0');
  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(seconds)}`;
  }
  return `${minutes}:${pad(seconds)}`;
};

/** Formats an ISO-UTC timestamp as a short local string, or `—` when null. */
export const formatLocalDateTime = (iso: string | null | undefined): string => {
  const d = parseUtcInstant(iso);
  if (!d) {
    return '—';
  }
  return d.toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
};
